import { useState, type FormEvent } from 'react';
import { KeyRound, ShieldAlert } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { signIn, signInWithPasskey, verifyBackupCode, verifyTotp } from '@/api/auth';

interface Props {
  onSignedIn: () => void;
}

export function SignInPage({ onSignedIn }: Props) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [twoFactorRequired, setTwoFactorRequired] = useState(false);
  const [twoFactorMode, setTwoFactorMode] = useState<'totp' | 'backup'>('totp');
  const [twoFactorCode, setTwoFactorCode] = useState('');
  const [trustDevice, setTrustDevice] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const result = await signIn(email, password);
      if (result.twoFactorRedirect) {
        setTwoFactorMode(result.twoFactorMethods.includes('totp') ? 'totp' : 'backup');
        setTwoFactorRequired(true);
        return;
      }
      onSignedIn();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Sign-in failed');
    } finally {
      setBusy(false);
    }
  }

  async function onTwoFactorSubmit(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      if (twoFactorMode === 'backup') {
        await verifyBackupCode(twoFactorCode, trustDevice);
      } else {
        await verifyTotp(twoFactorCode, trustDevice);
      }
      onSignedIn();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Verification failed');
    } finally {
      setBusy(false);
    }
  }

  async function onPasskeySignIn() {
    setBusy(true);
    setError(null);
    try {
      await signInWithPasskey();
      onSignedIn();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Passkey sign-in failed');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="flex min-h-svh items-center justify-center bg-background p-6">
      <Card className="w-full max-w-sm">
        <CardHeader className="space-y-3">
          <div className="flex size-10 items-center justify-center rounded-lg bg-destructive text-destructive-foreground">
            <ShieldAlert className="size-5" />
          </div>
          <CardTitle>Flowlib admin</CardTitle>
          <CardDescription>Restricted to backoffice staff.</CardDescription>
        </CardHeader>
        <CardContent>
          {twoFactorRequired ? (
            <form onSubmit={onTwoFactorSubmit} className="grid gap-4">
              <div className="grid gap-2">
                <Label htmlFor="two-factor-code">
                  {twoFactorMode === 'backup' ? 'Backup code' : 'Verification code'}
                </Label>
                <Input
                  id="two-factor-code"
                  type="text"
                  inputMode={twoFactorMode === 'backup' ? 'text' : 'numeric'}
                  pattern={twoFactorMode === 'backup' ? undefined : '[0-9]*'}
                  maxLength={twoFactorMode === 'backup' ? 24 : 6}
                  value={twoFactorCode}
                  onChange={(e) => setTwoFactorCode(e.target.value)}
                  autoComplete="one-time-code"
                  className="text-center font-mono text-lg tracking-widest"
                  required
                  autoFocus
                />
              </div>
              <label className="flex items-center gap-2 text-sm text-muted-foreground">
                <input
                  type="checkbox"
                  className="size-4 rounded border-input"
                  checked={trustDevice}
                  onChange={(e) => setTrustDevice(e.target.checked)}
                />
                Trust this device
              </label>
              {error && (
                <p className="text-sm text-destructive" role="alert">
                  {error}
                </p>
              )}
              <Button type="submit" disabled={busy}>
                {busy ? 'Verifying...' : 'Verify'}
              </Button>
              <Button
                type="button"
                variant="ghost"
                disabled={busy}
                onClick={() => {
                  setTwoFactorMode((mode) => (mode === 'totp' ? 'backup' : 'totp'));
                  setTwoFactorCode('');
                  setError(null);
                }}
              >
                {twoFactorMode === 'totp' ? 'Use backup code' : 'Use authenticator code'}
              </Button>
            </form>
          ) : (
            <form onSubmit={onSubmit} className="grid gap-4">
              <div className="grid gap-2">
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  autoComplete="username webauthn"
                  required
                />
              </div>
              <div className="grid gap-2">
                <Label htmlFor="password">Password</Label>
                <Input
                  id="password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  autoComplete="current-password webauthn"
                  required
                />
              </div>
              {error && (
                <p className="text-sm text-destructive" role="alert">
                  {error}
                </p>
              )}
              <Button type="submit" disabled={busy}>
                {busy ? 'Signing in...' : 'Sign in'}
              </Button>
              <Button
                type="button"
                variant="outline"
                disabled={busy}
                onClick={() => void onPasskeySignIn()}
              >
                <KeyRound className="mr-2 size-4" />
                Sign in with passkey
              </Button>
            </form>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
